import { createAction, createReducer, PayloadAction } from '@reduxjs/toolkit';
import { isProgressComplete } from 'reactApp/modules/uploadList/uploadList.getters';
import { setGroupedByFolderInputFiles } from 'reactApp/modules/uploadList/uploadList.module.helper';

import {
    EProgressStatus,
    IChangeItem,
    IInputFile,
    INormalizedInputFile,
    ISelectedError,
    IUpdateInputFile,
    IUploadListState,
} from './uploadList.model';

const initialListState: IUploadListState = {
    showUploader: false,
    hasInput: false,
    groupedFiles: {},
    progress: {
        percentLoaded: 0,
        uploadPacket: 0,
        status: EProgressStatus.NONE,
    },
    errors: {
        selectedFile: null,
        highlightSelectedFile: false,
        userFileSizeLimit: 0,
    },
};

export const setShowUploaderAction = createAction<boolean>('uploaderList/setShowUploaderAction');
export const setInputFilesAction = createAction<IInputFile[]>('uploaderList/setInputFilesAction');
export const setHasInputAction = createAction<boolean>('uploaderList/setHasInputAction');
export const updateUploadFilesAction = createAction<IUpdateInputFile | IUpdateInputFile[]>('uploaderList/updateUploadFilesAction');
export const deferredUpdateUploadFileAction = createAction<IUpdateInputFile | IUpdateInputFile[]>(
    'uploaderList/deferredUpdateUploadFileAction'
);
export const cancelAllUploadFileAction = createAction('uploaderList/cancelAllUploadFileAction');
export const removeUploadFilesAction = createAction<IChangeItem[]>('uploaderList/removeUploadFilesAction');
export const setProgressStatusAction = createAction<{ status: EProgressStatus }>('uploaderList/setProgressStatusAction');
export const setClearStateAction = createAction('uploaderList/setClearStateAction');
export const setSelectedErrorAction = createAction<ISelectedError>('uploaderList/setSelectedErrorAction');
export const setHighlightSelectedErrorAction = createAction<boolean>('uploaderList/setHighlightSelectedErrorAction');
export const setHighlightProblemFilesAction = createAction('uploaderList/setHighlightProblemFilesAction');
export const setUserFileSizeLimit = createAction<number>('uploaderList/setUserFileSizeLimit');
export const setPercentLoaded = createAction<number>('uploaderList/setPercentLoaded');
export const setGroupedFiles = createAction<INormalizedInputFile>('uploaderList/setGroupedFiles');

export const uploadDescriptor = createAction<{ name: string; }>('uploaderList/uploadDescriptor');

export const uploadListReducer = createReducer(initialListState, {
    [setShowUploaderAction.type]: (state, action: PayloadAction<boolean>) => {
        const { payload } = action;
        state.showUploader = payload;
    },
    [setInputFilesAction.type]: (state, action: PayloadAction<IInputFile[]>) => {
        const { payload } = action;

        // предыдущая загрузка закончилась, но пользователь добавил еще файлы
        if (isProgressComplete(state.progress.status)) {
            state.progress.percentLoaded = 0;
            state.progress.uploadPacket++;
        }

        state.hasInput = true;

        setGroupedByFolderInputFiles(state, payload);
    },
    [updateUploadFilesAction.type]: (state, action: PayloadAction<IUpdateInputFile | IUpdateInputFile[]>) => {
        const { payload } = action;
        setGroupedByFolderInputFiles(state, Array.isArray(payload) ? payload : [payload]);
    },
    [cancelAllUploadFileAction.type]: (state) => {
        state.progress.percentLoaded = 100;
    },
    [setProgressStatusAction.type]: (state, action: PayloadAction<{ status: EProgressStatus }>) => {
        const { payload } = action;
        state.progress.status = payload.status;
    },
    [setClearStateAction.type]: (state) => {
        state.showUploader = initialListState.showUploader;
        state.hasInput = initialListState.hasInput;
        state.groupedFiles = initialListState.groupedFiles;
        state.progress = initialListState.progress;
        state.errors = initialListState.errors;
    },
    [setSelectedErrorAction.type]: (state, action: PayloadAction<ISelectedError>) => {
        const { payload } = action;
        state.errors.selectedFile = payload.file;
    },
    [setHighlightSelectedErrorAction.type]: (state, action: PayloadAction<boolean>) => {
        const { payload } = action;
        state.errors.highlightSelectedFile = payload;
    },
    [setUserFileSizeLimit.type]: (state, action: PayloadAction<number>) => {
        const { payload } = action;
        state.errors.userFileSizeLimit = payload;
    },
    [setPercentLoaded.type]: (state, action: PayloadAction<number>) => {
        const { payload } = action;

        if (Number.isFinite(payload)) {
            // 99 потому что может быть раннее округление до 100, когда в конце загрузки файлы сильно меньшего размера чем в начале
            const percentLoaded = state.progress.status === EProgressStatus.COMPLETE ? 100 : Math.min(payload, 99);
            state.progress.percentLoaded = percentLoaded;
        }
    },
    [setGroupedFiles.type]: (state, action: PayloadAction<INormalizedInputFile>) => {
        const { payload } = action;
        state.groupedFiles = payload;
    },
});
